import { Fragment, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { components } from '@/types/api.generated';
import { PERMISSIONS } from '@/permissions';
import {
  PageShell,
  Badge,
  Button,
  Card,
  CardContent,
  ErrorState,
  Input,
  Label,
  LoadingPage,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@resource-kit/ui/react';
import { resourceKeys } from '@resource-kit/controllers';
import { useToast } from '@components/ui/Toast';
import { usePermissions } from '@hooks/useAuth';
import { NavGroupTabs } from '@components/layout/NavGroupTabs';
import { http } from '@lib/api';
import { driverProfileResource, listDriverProfiles, type DriverProfile } from './schema';

type LearnResult = components['schemas']['DriverProfileLearnResult'];
type OverridePatch = components['schemas']['DriverProfileOverridePatch'];
type OverrideResult = components['schemas']['DriverProfileOverrideResult'];
type PatchField = keyof OverridePatch;

interface PatchRequest {
  driverId: string;
  patch: OverridePatch;
}

interface ZoneDraft {
  zip: string;
  weight: string;
}

interface RangeDraft {
  p25: string;
  median: string;
  p75: string;
}

interface AnchorDraft {
  lat: string;
  lng: string;
  label: string;
}

const PROFILE_KEYS = resourceKeys(driverProfileResource.host.slug);

function formatWeight(weight: number): string {
  return weight.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
}

function CorrectionBadge({ corrected }: { corrected: boolean }) {
  const { t } = useTranslation();
  return corrected ? <Badge variant="secondary">{t('driverProfiles.corrected')}</Badge> : null;
}

function ZonesValue({ profile }: { profile: DriverProfile }) {
  const { t } = useTranslation();
  const zones = profile.effective.zones;
  if (zones === null || zones.length === 0) {
    return <span className="text-muted-foreground">{t('driverProfiles.noEffective')}</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {zones.slice(0, 5).map((zone) => (
        <span key={zone.zip} className="rounded-md border bg-muted/40 px-2 py-1 text-xs">
          {t('driverProfiles.zoneWeight', {
            zip: zone.zip,
            weight: formatWeight(zone.weight),
          })}
        </span>
      ))}
      <CorrectionBadge corrected={profile.override.zones !== null} />
    </div>
  );
}

function DailyRangeValue({ profile }: { profile: DriverProfile }) {
  const { t } = useTranslation();
  const range = profile.effective.daily_range;
  return (
    <div className="flex items-center gap-2">
      <span className={range === null ? 'text-muted-foreground' : undefined}>
        {range === null
          ? '—'
          : t('driverProfiles.dailyRangeValue', {
              p25: range.p25,
              median: range.median,
              p75: range.p75,
            })}
      </span>
      <CorrectionBadge corrected={profile.override.daily_range !== null} />
    </div>
  );
}

function EndAnchorValue({ profile }: { profile: DriverProfile }) {
  const { t } = useTranslation();
  const anchor = profile.effective.end_anchor;
  const hasLabel = anchor !== null && anchor.label.trim().length > 0;
  const coordinates =
    anchor === null
      ? undefined
      : t('driverProfiles.endAnchorValueCoordsOnly', {
          lat: anchor.lat,
          longitude: anchor.lng,
        });
  return (
    <div className="flex items-center gap-2">
      <span
        className={anchor === null ? 'text-muted-foreground' : undefined}
        title={hasLabel ? coordinates : undefined}
      >
        {anchor === null
          ? '—'
          : hasLabel
            ? t('driverProfiles.endAnchorValueWithLabel', { label: anchor.label })
            : coordinates}
      </span>
      <CorrectionBadge corrected={profile.override.end_anchor !== null} />
    </div>
  );
}

function OffsetValue({ profile }: { profile: DriverProfile }) {
  const { t } = useTranslation();
  const offset = profile.effective.departure_offset_min;
  return (
    <div className="flex items-center gap-2">
      <span>
        {offset > 0
          ? t('driverProfiles.offsetPositive', { value: offset })
          : t('driverProfiles.offsetValue', { value: offset })}
      </span>
      <CorrectionBadge corrected={profile.override.departure_offset_min !== null} />
    </div>
  );
}

interface OverrideEditorProps {
  profile: DriverProfile;
  pending: boolean;
  onCancel: () => void;
  onPatch: (patch: OverridePatch) => void;
}

function OverrideEditor({ profile, pending, onCancel, onPatch }: OverrideEditorProps) {
  const { t } = useTranslation();
  const [dirty, setDirty] = useState<ReadonlySet<PatchField>>(new Set());
  const [validationError, setValidationError] = useState<string | null>(null);
  const [zones, setZones] = useState<ZoneDraft[]>(() =>
    (profile.override.zones ?? []).map((zone) => ({
      zip: zone.zip,
      weight: String(zone.weight),
    })),
  );
  const [dailyRange, setDailyRange] = useState<RangeDraft>(() => ({
    p25: profile.override.daily_range?.p25.toString() ?? '',
    median: profile.override.daily_range?.median.toString() ?? '',
    p75: profile.override.daily_range?.p75.toString() ?? '',
  }));
  const [endAnchor, setEndAnchor] = useState<AnchorDraft>(() => ({
    lat: profile.override.end_anchor?.lat.toString() ?? '',
    lng: profile.override.end_anchor?.lng.toString() ?? '',
    label: profile.override.end_anchor?.label ?? '',
  }));
  const [departureOffset, setDepartureOffset] = useState(
    profile.override.departure_offset_min?.toString() ?? '',
  );

  const markDirty = (field: PatchField) => {
    setDirty((current) => new Set(current).add(field));
    setValidationError(null);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const patch: OverridePatch = {};

    if (dirty.has('override_zones')) {
      const parsedZones = zones.map((zone) => ({
        zip: zone.zip,
        weight: Number(zone.weight),
      }));
      if (
        parsedZones.length > 50 ||
        parsedZones.some(
          (zone) =>
            !/^\d{5}$/.test(zone.zip) ||
            !Number.isFinite(zone.weight) ||
            zone.weight <= 0 ||
            zone.weight > 1,
        )
      ) {
        setValidationError(t('driverProfiles.validation.zones'));
        return;
      }
      patch.override_zones = parsedZones;
    }

    if (dirty.has('override_daily_range')) {
      const range = {
        p25: Number(dailyRange.p25),
        median: Number(dailyRange.median),
        p75: Number(dailyRange.p75),
      };
      if (
        Object.values(range).some((value) => !Number.isInteger(value) || value <= 0) ||
        range.p25 > range.median ||
        range.median > range.p75
      ) {
        setValidationError(t('driverProfiles.validation.dailyRange'));
        return;
      }
      patch.override_daily_range = range;
    }

    if (dirty.has('override_end_anchor')) {
      const anchor = {
        lat: Number(endAnchor.lat),
        lng: Number(endAnchor.lng),
        label: endAnchor.label,
      };
      if (
        endAnchor.lat === '' ||
        endAnchor.lng === '' ||
        !Number.isFinite(anchor.lat) ||
        anchor.lat < -90 ||
        anchor.lat > 90 ||
        !Number.isFinite(anchor.lng) ||
        anchor.lng < -180 ||
        anchor.lng > 180 ||
        anchor.label.length > 120
      ) {
        setValidationError(t('driverProfiles.validation.endAnchor'));
        return;
      }
      patch.override_end_anchor = anchor;
    }

    if (dirty.has('departure_offset_min')) {
      const offset = Number(departureOffset);
      if (departureOffset === '' || !Number.isInteger(offset) || offset < -120 || offset > 120) {
        setValidationError(t('driverProfiles.validation.departureOffset'));
        return;
      }
      patch.departure_offset_min = offset;
    }

    if (dirty.size === 0) {
      onCancel();
      return;
    }
    onPatch(patch);
  };

  const clearButton = (field: PatchField, fieldLabel: string) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={pending}
      aria-label={t('driverProfiles.clearAria', {
        field: fieldLabel,
        driver: profile.driver_name,
      })}
      onClick={() => onPatch({ [field]: null })}
    >
      {t('driverProfiles.clearRestore')}
    </Button>
  );

  return (
    <form
      className="space-y-6 py-4"
      aria-label={t('driverProfiles.editFormAria', { driver: profile.driver_name })}
      onSubmit={submit}
    >
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-medium">{t('driverProfiles.field.zones')}</h3>
          <CorrectionBadge corrected={profile.override.zones !== null} />
          {profile.override.zones !== null &&
            clearButton('override_zones', t('driverProfiles.clearField.zones'))}
        </div>
        {zones.map((zone, index) => (
          <div key={index} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <Input
              value={zone.zip}
              inputMode="numeric"
              maxLength={5}
              aria-label={t('driverProfiles.zoneZipAria', { index: index + 1 })}
              onChange={(event) => {
                setZones((current) =>
                  current.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, zip: event.target.value } : item,
                  ),
                );
                markDirty('override_zones');
              }}
            />
            <Input
              type="number"
              min="0.000001"
              max="1"
              step="any"
              value={zone.weight}
              aria-label={t('driverProfiles.zoneWeightAria', { index: index + 1 })}
              onChange={(event) => {
                setZones((current) =>
                  current.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, weight: event.target.value } : item,
                  ),
                );
                markDirty('override_zones');
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('driverProfiles.removeZoneAria', { index: index + 1 })}
              onClick={() => {
                setZones((current) => current.filter((_, itemIndex) => itemIndex !== index));
                markDirty('override_zones');
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={zones.length >= 50}
          onClick={() => {
            setZones((current) => [...current, { zip: '', weight: '' }]);
            markDirty('override_zones');
          }}
        >
          <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
          {t('driverProfiles.addZone')}
        </Button>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-medium">{t('driverProfiles.field.dailyRange')}</h3>
          <CorrectionBadge corrected={profile.override.daily_range !== null} />
          {profile.override.daily_range !== null &&
            clearButton('override_daily_range', t('driverProfiles.clearField.dailyRange'))}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {(['p25', 'median', 'p75'] as const).map((field) => (
            <div key={field} className="space-y-2">
              <Label htmlFor={`${profile.driver_id}-${field}`}>
                {t(`driverProfiles.field.${field}`)}
              </Label>
              <Input
                id={`${profile.driver_id}-${field}`}
                type="number"
                min="1"
                step="1"
                value={dailyRange[field]}
                onChange={(event) => {
                  setDailyRange((current) => ({
                    ...current,
                    [field]: event.target.value,
                  }));
                  markDirty('override_daily_range');
                }}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-medium">{t('driverProfiles.field.endAnchor')}</h3>
          <CorrectionBadge corrected={profile.override.end_anchor !== null} />
          {profile.override.end_anchor !== null &&
            clearButton('override_end_anchor', t('driverProfiles.clearField.endAnchor'))}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor={`${profile.driver_id}-label`}>
              {t('driverProfiles.field.address')}
            </Label>
            <Input
              id={`${profile.driver_id}-label`}
              type="text"
              maxLength={120}
              placeholder={t('driverProfiles.endAnchorAddressPlaceholder')}
              value={endAnchor.label}
              onChange={(event) => {
                setEndAnchor((current) => ({
                  ...current,
                  label: event.target.value,
                }));
                markDirty('override_end_anchor');
              }}
            />
          </div>
          {(['lat', 'lng'] as const).map((field) => (
            <div key={field} className="space-y-2">
              <Label htmlFor={`${profile.driver_id}-${field}`}>
                {t(`driverProfiles.field.${field}`)}
              </Label>
              <Input
                id={`${profile.driver_id}-${field}`}
                type="number"
                min={field === 'lat' ? '-90' : field === 'lng' ? '-180' : undefined}
                max={field === 'lat' ? '90' : field === 'lng' ? '180' : undefined}
                step="any"
                value={endAnchor[field]}
                onChange={(event) => {
                  setEndAnchor((current) => ({
                    ...current,
                    [field]: event.target.value,
                  }));
                  markDirty('override_end_anchor');
                }}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Label htmlFor={`${profile.driver_id}-departure-offset`}>
            {t('driverProfiles.field.departureOffset')}
          </Label>
          <CorrectionBadge corrected={profile.override.departure_offset_min !== null} />
          {profile.override.departure_offset_min !== null &&
            clearButton('departure_offset_min', t('driverProfiles.clearField.departureOffset'))}
        </div>
        <Input
          id={`${profile.driver_id}-departure-offset`}
          className="max-w-xs"
          type="number"
          min="-120"
          max="120"
          step="1"
          value={departureOffset}
          onChange={(event) => {
            setDepartureOffset(event.target.value);
            markDirty('departure_offset_min');
          }}
        />
      </section>

      {validationError !== null && (
        <p role="alert" className="text-sm text-destructive">
          {validationError}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" disabled={pending} onClick={onCancel}>
          {t('ui.cancel')}
        </Button>
        <Button type="submit" disabled={pending}>
          {t(pending ? 'driverProfiles.saving' : 'driverProfiles.save')}
        </Button>
      </div>
    </form>
  );
}

export function DriverProfilesPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [expandedDriverId, setExpandedDriverId] = useState<string | null>(null);
  const canWrite = permissions.includes(PERMISSIONS.DISPATCH_WRITE);
  const profiles = useQuery({
    queryKey: PROFILE_KEYS.all(),
    queryFn: listDriverProfiles,
  });
  const relearn = useMutation({
    mutationFn: () => http.post<LearnResult>('/api/driver-profiles/relearn'),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: PROFILE_KEYS.all() });
      toast.success(
        t('driverProfiles.relearnSuccessTitle'),
        t('driverProfiles.relearnSuccess', {
          drivers: result.driver_count,
          jobs: result.job_count,
        }),
      );
    },
    onError: (error: Error) => {
      toast.error(t('driverProfiles.relearnErrorTitle'), error.message);
    },
  });
  const patchOverride = useMutation({
    mutationFn: ({ driverId, patch }: PatchRequest) =>
      http.patch<OverrideResult>(
        `/api/driver-profiles/${encodeURIComponent(driverId)}/override`,
        patch,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PROFILE_KEYS.all() });
      setExpandedDriverId(null);
      toast.success(t('driverProfiles.saveSuccessTitle'), t('driverProfiles.saveSuccess'));
    },
    onError: (error: Error) => {
      toast.error(t('driverProfiles.saveErrorTitle'), error.message);
    },
  });

  const rows = profiles.data?.items ?? [];

  return (
    <PageShell
      title={t('driverProfiles.listTitle')}
      subtitle={t('driverProfiles.listDescription')}
      tabs={<NavGroupTabs group="drivers" />}
      actions={
        canWrite ? (
          <Button
            onClick={() => relearn.mutate()}
            disabled={relearn.isPending}
            aria-busy={relearn.isPending}
          >
            <RefreshCw
              className={`mr-1 h-4 w-4 ${relearn.isPending ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            {t(relearn.isPending ? 'driverProfiles.relearning' : 'driverProfiles.relearn')}
          </Button>
        ) : undefined
      }
    >
      {profiles.isPending && <LoadingPage />}
      {profiles.isError && (
        <ErrorState
          message={profiles.error.message}
          action={
            <Button variant="outline" onClick={() => profiles.refetch()}>
              {t('ui.retry')}
            </Button>
          }
        />
      )}
      {profiles.isSuccess && (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('driverProfiles.field.driver')}</TableHead>
                  <TableHead>{t('driverProfiles.field.zones')}</TableHead>
                  <TableHead>{t('driverProfiles.field.dailyRange')}</TableHead>
                  <TableHead>{t('driverProfiles.field.endAnchor')}</TableHead>
                  <TableHead>{t('driverProfiles.field.departureOffset')}</TableHead>
                  {canWrite && <TableHead className="w-16" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 6 : 5}
                      className="h-24 text-center text-muted-foreground"
                    >
                      {t('driverProfiles.empty')}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((profile) => {
                  const expanded = expandedDriverId === profile.driver_id;
                  return (
                    <Fragment key={profile.driver_id}>
                      <TableRow>
                        <TableCell>
                          <div className="font-medium">{profile.driver_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {profile.learned.from === null
                              ? t('driverProfiles.coldStart')
                              : t('driverProfiles.provenance', {
                                  count: profile.learned.from.job_count,
                                  start: profile.learned.from.window_start,
                                  end: profile.learned.from.window_end,
                                })}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-normal">
                          <ZonesValue profile={profile} />
                        </TableCell>
                        <TableCell>
                          <DailyRangeValue profile={profile} />
                        </TableCell>
                        <TableCell>
                          <EndAnchorValue profile={profile} />
                        </TableCell>
                        <TableCell>
                          <OffsetValue profile={profile} />
                        </TableCell>
                        {canWrite && (
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-expanded={expanded}
                              aria-label={t('driverProfiles.editAria', {
                                driver: profile.driver_name,
                              })}
                              onClick={() =>
                                setExpandedDriverId(expanded ? null : profile.driver_id)
                              }
                            >
                              {expanded ? (
                                <ChevronDown className="h-4 w-4 rotate-180" aria-hidden="true" />
                              ) : (
                                <Pencil className="h-4 w-4" aria-hidden="true" />
                              )}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                      {expanded && (
                        <TableRow>
                          <TableCell colSpan={6} className="whitespace-normal bg-muted/20 px-6">
                            <OverrideEditor
                              profile={profile}
                              pending={patchOverride.isPending}
                              onCancel={() => setExpandedDriverId(null)}
                              onPatch={(patch) =>
                                patchOverride.mutate({ driverId: profile.driver_id, patch })
                              }
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </PageShell>
  );
}
